import { jsPDF } from 'jspdf';
import moment from 'moment';
import { formatOrderItemSku } from './order_item_helper';
import { getOrderItemName } from '../components/OrderItemsCell';

const formatInr = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
};

const BRAND = 'Careveli';
const SELLER_LINES = [
  'Careveli',
  'GST-compliant tax invoice (generated from order data)',
];

const getShippingAddress = (order) => order?.shippingAddress || order?.address || {};

const formatAddressBlock = (order) => {
  const addr = getShippingAddress(order);
  const name = [addr.firstName, addr.lastName].filter(Boolean).join(' ') || '—';
  const lines = [
    name,
    addr.street,
    [addr.city, addr.state, addr.zipcode].filter(Boolean).join(', '),
    addr.country,
    addr.email,
    addr.phone ? `Phone: ${addr.phone}` : null,
  ].filter((line) => line && String(line).trim());
  return lines;
};

const paymentModeLabel = (order) => {
  const method = order?.paymentMethod || order?.paymentMode || '—';
  const status = order?.paymentStatus;
  if (status) return `${method} (${status})`;
  return String(method);
};

/**
 * Build a branded PDF invoice in the browser when the server PDF is unavailable.
 * @param {object} order — full admin order document
 * @returns {{ filename: string, blob: Blob }}
 */
export const generateClientInvoicePdf = (order) => {
  if (!order) {
    throw new Error('Order is required to generate invoice PDF');
  }

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  let y = margin;
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - margin * 2;

  const invoiceNo =
    order.invoiceNumber || order.orderNumber || order._id || 'DRAFT';
  const invoiceDate = order.invoiceDate || order.createdAt || new Date().toISOString();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(33, 37, 41);
  doc.text(BRAND, margin, y);
  y += 22;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(90, 90, 90);
  SELLER_LINES.forEach((line) => {
    doc.text(line, margin, y);
    y += 14;
  });

  y += 8;
  doc.setTextColor(33, 37, 41);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('Tax Invoice', pageWidth - margin, margin, { align: 'right' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Invoice No: ${invoiceNo}`, pageWidth - margin, margin + 18, { align: 'right' });
  doc.text(
    `Date: ${moment(invoiceDate).format('DD MMM YYYY')}`,
    pageWidth - margin,
    margin + 32,
    { align: 'right' }
  );
  if (order.orderNumber) {
    doc.text(`Order: ${order.orderNumber}`, pageWidth - margin, margin + 46, { align: 'right' });
  }

  y = Math.max(y, margin + 58);
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, pageWidth - margin, y);
  y += 16;

  doc.setFont('helvetica', 'bold');
  doc.text('Bill to / Ship to', margin, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  formatAddressBlock(order).forEach((line) => {
    doc.text(String(line), margin, y, { maxWidth: contentWidth * 0.55 });
    y += 13;
  });

  y += 10;
  doc.setFont('helvetica', 'bold');
  doc.text('Payment mode', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(paymentModeLabel(order), margin + 90, y);
  y += 20;

  const colX = {
    item: margin,
    sku: margin + 200,
    qty: margin + 280,
    price: margin + 320,
    total: margin + 400,
  };

  const drawTableHeader = () => {
    doc.setFillColor(245, 245, 245);
    doc.rect(margin, y - 10, contentWidth, 18, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Item', colX.item, y);
    doc.text('SKU', colX.sku, y);
    doc.text('Qty', colX.qty, y);
    doc.text('Price', colX.price, y);
    doc.text('Total', colX.total, y);
    y += 12;
    doc.setFont('helvetica', 'normal');
  };

  drawTableHeader();

  const items = order.items || [];
  items.forEach((item) => {
    if (y > 700) {
      doc.addPage();
      y = margin;
      drawTableHeader();
    }
    const name = getOrderItemName(item);
    const sku = formatOrderItemSku(item);
    const qty = Number(item.quantity) || 1;
    const unit = Number(item.price) || 0;
    const lineTotal = unit * qty;

    doc.setFontSize(9);
    const nameLines = doc.splitTextToSize(name, 185);
    doc.text(nameLines, colX.item, y);
    doc.text(sku, colX.sku, y);
    doc.text(String(qty), colX.qty, y);
    doc.text(formatInr(unit), colX.price, y);
    doc.text(formatInr(lineTotal), colX.total, y);
    y += Math.max(14, nameLines.length * 12);
  });

  if (!items.length) {
    doc.text('No line items', colX.item, y);
    y += 14;
  }

  y += 10;
  doc.line(margin, y, pageWidth - margin, y);
  y += 16;

  const summaryX = pageWidth - margin - 160;
  const addSummaryRow = (label, value, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.text(label, summaryX, y);
    doc.text(value, pageWidth - margin, y, { align: 'right' });
    y += 14;
  };

  addSummaryRow('Subtotal', formatInr(order.subtotal || 0));
  addSummaryRow('Shipping', formatInr(order.shippingFee || 0));
  const discount = Number(order.discountAmount || 0);
  if (discount > 0) {
    addSummaryRow('Discount', `-${formatInr(discount)}`);
  }

  const gst = order.gstBreakup || {};
  if (gst.taxableValue != null) {
    addSummaryRow('Taxable value', formatInr(gst.taxableValue));
  }
  if (gst.cgst != null && Number(gst.cgst) > 0) {
    addSummaryRow('CGST', formatInr(gst.cgst));
  }
  if (gst.sgst != null && Number(gst.sgst) > 0) {
    addSummaryRow('SGST', formatInr(gst.sgst));
  }
  if (gst.igst != null && Number(gst.igst) > 0) {
    addSummaryRow('IGST', formatInr(gst.igst));
  }
  if (gst.supplyType) {
    addSummaryRow('Supply type', String(gst.supplyType));
  }

  y += 4;
  addSummaryRow(
    'Grand total',
    formatInr(gst.grandTotal ?? order.total ?? order.amount ?? 0),
    true
  );

  y += 24;
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(
    'This document was generated by the Careveli admin panel. For official GST records, use the server-issued invoice when available.',
    margin,
    y,
    { maxWidth: contentWidth }
  );

  const filenameBase = String(invoiceNo).replace(/\//g, '-');
  const filename = `Invoice-${filenameBase}.pdf`;
  const blob = doc.output('blob');

  return { filename, blob };
};

export const downloadClientInvoicePdf = (order) => {
  const { filename, blob } = generateClientInvoicePdf(order);
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
  return { filename, blob, source: 'client' };
};
